import React, { useState, useEffect } from "react";
import {
  FaDownload,
  FaCheckCircle,
  FaSpinner,
  FaTimes,
  FaUpload,
  FaUser,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaEye,
  FaTag,
  FaExclamationTriangle,
  FaRedo,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DescriptionViewer from "../../Common/DescriptionViewer";
import AdsSubNav from '../../Common/AdsSubNav';

const AcceptedFreeAds = () => {
  const [ads, setAds] = useState([]);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(ads.length / rowsPerPage);
  // Pagination logic
  const paginatedAds = ads.length > 0 ? [...ads].reverse().slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage) : [];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }
  const [loading, setLoading] = useState(true);
  const [downloadAdId, setDownloadAdId] = useState(null);
  const [isDownloadChecked, setIsDownloadChecked] = useState(false);
  const [error, setError] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [formData, setFormData] = useState({
    adId: "",
    screenshot: null,
    channelName: "",
    channelLink: "",
    platformName: "",
    adTiming: "",
  });
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const navigate = useNavigate();

  // State for fresh data fetch
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchAcceptedFreeAds = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/influencer/get/free/accepted/ads`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Response of Accepted Free Ads:", response.data);
      const adsData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data.data)
          ? response.data.data
          : Array.isArray(response.data.advertisements)
            ? response.data.advertisements
            : [];
      setAds(adsData);
      setError(null);
    } catch (err) {
      setError("Failed to load accepted free ads. Please try again later.");
      toast.error("Failed to load accepted free ads");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchAcceptedFreeAds();
  };

  useEffect(() => {
    fetchAcceptedFreeAds();
  }, []);

  useEffect(() => {
    if (showModal || previewMedia || navigating) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    // Cleanup on unmount
    return () => document.body.classList.remove("modal-open");
  }, [showModal, previewMedia, navigating]);

  useEffect(() => {
    const fetchAdPricing = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/ad/pricing`
        );
        setPlatforms(res.data.data.plateforms || []);
      } catch (err) {
        console.error("Error fetching ad pricing:", err);

      }
    };
    fetchAdPricing();
  }, []);

  const handleDownload = async (ad) => {
    if (!ad) {
      toast.error("No advertisement data provided.");
      return;
    }
    try {
      const mediaType = ad.mediaType?.toLowerCase();
      const url = mediaType === "video" ? ad.videoUrl : ad.imageUrl;
      if (!url) {
        toast.error("Media URL is missing.");
        return;
      }
      const fileName = `freead_${ad._id}.${mediaType === "video" ? "mp4" : "jpg"}`;
      
      const isApp = navigator.userAgent.includes('iInsafApp');
      if (isApp) {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Download starting in app...");
        return;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch file: ${response.statusText}`);
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      toast.success("Advertisement downloaded successfully!");
    } catch (err) {
      toast.error("Failed to download advertisement");
    } finally {
      setDownloadAdId(null);
      setIsDownloadChecked(false);
    }
  };

  const handleConfirmUploads = (adId) => {
    setFormData((prev) => ({ ...prev, adId }));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
    setFormData({
      adId: "",
      screenshot: null,
      channelName: "",
      channelLink: "",
      platformName: "",
      adTiming: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "adTiming") {
      let cleanValue = value.replace(/[^\d:]/g, "");
      const prevValue = formData.adTiming;
      const isDeleting = value.length < prevValue.length;
      if (value === "") {
        setFormData((prev) => ({ ...prev, [name]: "" }));
        return;
      }
      if (isDeleting && cleanValue.includes(":") && cleanValue.length <= 3) {
        cleanValue = cleanValue.replace(":", "");
      }
      if (!isDeleting && cleanValue.length === 2 && !cleanValue.includes(":")) {
        cleanValue = cleanValue + ":";
      }
      if (cleanValue.replace(":", "").length === 0) {
        cleanValue = "";
      }
      if (cleanValue.length > 5) {
        cleanValue = cleanValue.substring(0, 5);
      }
      const mmssPattern = /^([0-5]?\d):([0-5]?\d)$/;
      if (cleanValue.length === 5 && !mmssPattern.test(cleanValue)) {
        toast.error("Please enter time in MM:SS format (e.g., 01:30)");
      }
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      setFormData((prev) => ({ ...prev, screenshot: file }));
      const previewUrl = URL.createObjectURL(file);
      setScreenshotPreview(previewUrl);
    }
  };

  const removeScreenshot = () => {
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
    setFormData((prev) => ({ ...prev, screenshot: null }));
    const fileInput = document.querySelector('input[name="screenshot"]');
    if (fileInput) fileInput.value = "";
  };

  const handleSubmitConfirmation = async (e) => {
    e.preventDefault();
    if (
      !formData.screenshot ||
      !formData.channelName ||
      !formData.channelLink ||
      !formData.platformName ||
      !formData.adTiming ||
      !formData.adId
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    // YouTube validation - only allow standard video links
    if (formData.platformName.toLowerCase() === 'youtube') {
      // Valid YouTube video URL patterns
      const youtubeVideoPattern = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[a-zA-Z0-9_-]{11}(?:\S+)?$/;

      if (!youtubeVideoPattern.test(formData.channelLink)) {
        toast.error("Please provide a valid YouTube video link (e.g., https://www.youtube.com/watch?v=VIDEO_ID)");
        return;
      }

      // Specifically reject shorts and channel links
      if (formData.channelLink.includes('youtube.com/shorts') ||
        formData.channelLink.includes('youtube.com/channel') ||
        formData.channelLink.includes('youtube.com/c/') ||
        formData.channelLink.includes('youtube.com/user/')) {
        toast.error("YouTube shorts and channel links are not accepted. Please provide a standard YouTube video link.");
        return;
      }
    }

    const platformLinkPatterns = {
      youtube: /youtube\.com\/watch\?v=|youtu\.be\//i,
      facebook: /facebook\.com/i,
      instagram: /instagram\.com/i,
      twitter: /twitter\.com|x\.com/i,
      linkedin: /linkedin\.com/i,
      tiktok: /tiktok\.com/i,
      sharechat: /sharechat\.com/i,
      moj: /mojapp\.in/i,
      snapchat: /snapchat\.com/i,
    };

    const selectedPlatform = formData.platformName.trim().toLowerCase();

    if (
      platformLinkPatterns[selectedPlatform] &&
      !platformLinkPatterns[selectedPlatform].test(formData.channelLink)
    ) {
      toast.error(`For ${formData.platformName} platform, please provide a valid ${formData.platformName} video link.`);
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append("adId", formData.adId);
      submitData.append("screenshot", formData.screenshot);
      submitData.append("channelName", formData.channelName);
      submitData.append("videoLink", formData.channelLink);
      submitData.append("platform", formData.platformName);
      submitData.append("duration", formData.adTiming);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/submit/proof/free/ad`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("Response of submit proof Free Ads:", response);
      if (response.status === 200) {
        toast.success("Upload confirmation submitted successfully!");
      }
      setAds((prevAds) => prevAds.filter((ad) => (ad._id || ad.id) !== formData.adId));
      handleCloseModal();
      setNavigating(true);
      setTimeout(() => {
        navigate("/reporter/freeads/running");
      }, 1500);
    } catch (error) {
      if (error.response) {
        const errorMessage = error.response.data?.message || "Server error occurred";
        toast.error(`Failed to submit: ${errorMessage}`);
      } else if (error.request) {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error("Failed to submit confirmation. Please try again.");
      }
    }
  };

  const openMediaPreview = (mediaType, url) => {
    setPreviewMedia({
      type: mediaType,
      url: url
    });
  };

  const closeMediaPreview = () => {
    setPreviewMedia(null);
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pb-12">
      {/* Modern Header - Compact for Mobile */}
      <div className="max-w-7xl mx-auto mb-3 sm:mb-6 px-2 sm:px-4 mt-2 sm:mt-6">
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-xl sm:rounded-2xl shadow-xl p-2 sm:p-6 text-white relative overflow-hidden">
          {/* Decorative background element */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />

          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[9px] font-black uppercase tracking-tight border border-white/20 transition-all whitespace-nowrap shadow-sm"
              >
                ← Back
              </button>
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <div className="p-2 sm:p-3 bg-white/20 rounded-lg sm:rounded-xl backdrop-blur-md border border-white/30 hidden xs:flex">
                  <FaCheckCircle className="text-sm sm:text-2xl text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-sm sm:text-2xl md:text-3xl font-black tracking-tight truncate">Accepted Reward Ads</h1>
                  <p className="text-emerald-100/80 text-[9px] sm:text-sm font-bold uppercase tracking-wider mt-0.5 truncate">Active tasks awaiting proof</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="bg-white/20 backdrop-blur-md rounded-lg sm:rounded-xl p-2 sm:px-4 sm:py-3 border border-white/30 text-center flex-1 sm:flex-none min-w-[70px] sm:min-w-[100px] shadow-lg">
                <div className="text-sm sm:text-2xl font-black">{ads.length}</div>
                <div className="text-[8px] sm:text-xs font-black uppercase tracking-widest text-emerald-100">Tasks</div>
              </div>
              <button
                onClick={handleRefresh}
                disabled={loading || isRefreshing}
                className="p-2.5 sm:p-4 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-all border border-white/30 backdrop-blur-md text-white shadow-lg active:scale-95 group flex items-center justify-center"
              >
                <FaRedo className={`w-3 h-3 sm:w-4 sm:h-4 ${isRefreshing ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <AdsSubNav type="free" />
      {navigating && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin"
                style={{ borderWidth: "4px" }}
              />
              {/* Middle ring */}
              <span
                className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow"
                style={{ borderWidth: "4px", animationDuration: "2s" }}
              />
              {/* Inner ring */}
              <span
                className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower"
                style={{ borderWidth: "4px", animationDuration: "3s" }}
              />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Redirecting to Running Advertisements...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the status.
            </span>
          </div>
        </div>
      )}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <FaSpinner className="animate-spin text-green-500 text-4xl" />
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
        </div>
      )}
      {/* Grid Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
        {!loading && !error && (
          <>
            {ads.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-[2.5rem] border border-dashed border-slate-200 shadow-sm">
                <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaCheckCircle className="text-slate-300 text-5xl" />
                </div>
                <h3 className="text-slate-900 font-black text-2xl tracking-tight">No Active Tasks</h3>
                <p className="text-slate-400 text-sm font-bold mt-2 max-w-sm mx-auto">You haven't accepted any free ads yet. Browse the marketplace for new opportunities!</p>
                <button
                  onClick={() => navigate('/reporter/freeads/new')}
                  className="mt-8 px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-slate-900 transition-all active:scale-95 shadow-xl shadow-indigo-200"
                >
                  Browse Ads
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                {paginatedAds.map((ad, index) => (
                  <motion.div
                    key={ad._id || ad.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="bg-white rounded-[1.5rem] sm:rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/40 overflow-hidden group hover:border-emerald-200 transition-all flex flex-row md:flex-row relative"
                  >
                    {/* Media/Header Section */}
                    <div className="relative w-28 sm:w-32 md:w-40 lg:w-48 overflow-hidden bg-slate-900 shrink-0">
                      {ad.mediaType && ad.mediaType.toLowerCase() === 'video' ? (
                        <div
                          className="w-full h-full relative cursor-pointer"
                          onClick={() => openMediaPreview('video', ad.videoUrl)}
                        >
                          <video
                            src={ad.videoUrl}
                            className="w-full h-full object-cover opacity-80"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/30 group-hover:scale-110 transition-transform">
                              <FaEye size={12} />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={ad.imageUrl}
                          alt="ad"
                          className="w-full h-full object-cover opacity-80 group-hover:scale-110 transition-transform duration-700 cursor-pointer"
                          onClick={() => openMediaPreview('image', ad.imageUrl)}
                        />
                      )}
                    </div>

                    {/* Details Body */}
                    <div className="p-3 sm:p-4 md:p-5 flex flex-col flex-1 min-w-0 relative">
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3 z-10">
                        {ad.reporterEntry?.adminRejectNote ? (
                          <div className="px-2 py-0.5 bg-red-500 text-white text-[8px] sm:text-[10px] font-black rounded-md shadow-lg shadow-red-200 uppercase tracking-widest flex items-center gap-1 animate-pulse">
                            <FaExclamationTriangle size={8} /> Needs Fix
                          </div>
                        ) : (
                          <div className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[8px] sm:text-[10px] font-black rounded-md border border-emerald-200 uppercase tracking-widest">
                            {ad.status || "FREE"}
                          </div>
                        )}
                      </div>

                      <div className="mb-2">
                        <h3 className="text-slate-900 font-[1000] text-sm sm:text-base md:text-lg leading-tight line-clamp-1 pr-14">
                          {ad.adType || ad.type || ad.title || "Banner"}
                        </h3>
                        <div className="text-[10px] text-slate-400 font-mono mt-0.5 uppercase tracking-tighter">
                          ID: {String(ad._id || ad.id).slice(-8).toUpperCase()}
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <FaMapMarkerAlt className="text-indigo-500 shrink-0" size={10} />
                          <span className="text-[10px] sm:text-[11px] font-bold">
                            {ad.state ? (Array.isArray(ad.state) ? ad.state.filter(Boolean)[0] || "Global" : ad.state) : "Haryana"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <FaCalendarAlt className="text-orange-500 shrink-0" size={10} />
                          <span className="text-[10px] sm:text-[11px] font-bold">
                            {new Date(ad.createdAt).toLocaleDateString() || "11/18/2025"}
                          </span>
                        </div>
                      </div>

                      {/* Rejection Note if exists */}
                      {ad.reporterEntry?.adminRejectNote && (
                        <div className="mb-3 bg-red-50 border border-red-100 rounded-xl p-2">
                          <p className="text-[10px] text-red-700 font-bold italic leading-tight line-clamp-2">
                            "{ad.reporterEntry.adminRejectNote}"
                          </p>
                        </div>
                      )}

                      {/* Description Toggle */}
                      <div className="mb-4">
                        <div className="bg-slate-50 rounded-xl px-3 py-2 text-slate-500 text-[10px] sm:text-[11px] font-bold italic leading-relaxed flex items-center justify-between group/desc cursor-pointer hover:bg-slate-100 transition-colors">
                          <div className="truncate flex-1">
                            <DescriptionViewer
                              description={ad.description}
                              wordLimit={8}
                              textSize="text-[10px] sm:text-xs"
                            />
                          </div>
                          <FaEye className="text-indigo-400 ml-2 shrink-0 opacity-40 group-hover/desc:opacity-100 transition-opacity" size={12} />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-auto flex flex-col sm:flex-row gap-2">
                        <button
                          onClick={() => handleDownload(ad)}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all border border-slate-200 active:scale-95"
                        >
                          <FaDownload size={10} />
                          Download
                        </button>

                        <button
                          onClick={() => handleConfirmUploads(ad._id || ad.id)}
                          className={`flex-[2] flex items-center justify-center gap-2 py-2.5 ${ad.reporterEntry?.adminRejectNote
                            ? "bg-red-500 hover:bg-red-600 shadow-red-100"
                            : "bg-emerald-500 hover:bg-emerald-600 shadow-emerald-100"
                            } text-white rounded-xl font-black text-[10px] uppercase tracking-[0.1em] transition-all active:scale-95 shadow-lg flex items-center gap-2`}
                        >
                          <FaUpload size={10} />
                          {ad.reporterEntry?.adminRejectNote ? "Fix Proof" : "Confirm Upload Proof"}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-12 mb-20 px-4">
                <nav className="inline-flex items-center space-x-2 bg-white rounded-[1.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${currentPage === 1
                      ? 'bg-slate-50 text-slate-300 cursor-not-allowed'
                      : 'bg-white text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'
                      }`}
                  >
                    Prev
                  </button>
                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => handlePageChange(idx + 1)}
                      className={`w-10 h-10 rounded-xl text-xs font-black transition-all ${currentPage === idx + 1
                        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200 scale-110'
                        : 'bg-white text-slate-500 hover:bg-slate-50'
                        }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${currentPage === totalPages
                      ? 'bg-slate-50 text-slate-300 cursor-not-allowed'
                      : 'bg-white text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'
                      }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-xl mx-auto max-h-[96vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 sm:px-6 py-4 sm:py-5 text-white shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold">Upload Confirmation</h3>
                  <p className="text-blue-100 text-[10px] sm:text-xs">Provide details about your advertisement upload</p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-white hover:text-red-200 transition-colors duration-200 p-1.5 rounded-full hover:bg-white hover:bg-opacity-20"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              <form onSubmit={handleSubmitConfirmation} className="space-y-4">
                <div className="bg-green-50 rounded-xl p-4 sm:p-5">
                  <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-3 flex items-center">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center mr-2 sm:mr-3">
                      <span className="text-green-600 font-bold text-xs">1</span>
                    </div>
                    Screenshot Upload
                  </h4>
                  <div className="space-y-3">
                    <label className="block text-[11px] sm:text-sm font-bold text-gray-700 uppercase tracking-tight">Advertisement Screenshot *</label>
                    <div className="relative">
                      <input
                        type="file"
                        name="screenshot"
                        onChange={handleFileChange}
                        accept="image/*"
                        className="w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-dashed border-gray-300 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-200 file:mr-2 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-[10px] file:font-semibold file:bg-green-100 file:text-green-700 hover:file:bg-green-200"
                        required
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <FaUpload className="text-gray-400 size-3 sm:size-4" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-gray-500">
                      <span>JPG, PNG, GIF (Max 5MB)</span>
                      {formData.screenshot && (
                        <span className="text-green-600 font-medium">
                          ✓ {formData.screenshot.name} ({(formData.screenshot.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      )}
                    </div>
                    {screenshotPreview && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-semibold text-gray-700">Preview:</label>
                          <button
                            type="button"
                            onClick={removeScreenshot}
                            className="text-red-500 hover:text-red-700 transition-colors text-sm font-medium flex items-center space-x-1"
                          >
                            <FaTimes className="w-3 h-3" />
                            <span>Remove</span>
                          </button>
                        </div>
                        <div className="relative group">
                          <img
                            src={screenshotPreview}
                            alt="Screenshot preview"
                            className="w-full max-w-md mx-auto rounded-lg border-2 border-gray-200 "
                            style={{ maxHeight: "300px", objectFit: "contain" }}
                          />
                          <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs">
                            {formData.screenshot && `${formData.screenshot.name}`}
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-4 text-xs text-gray-600 bg-gray-100 p-3 rounded-lg"></div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 sm:p-5">
                  <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-3 flex items-center">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-full flex items-center justify-center mr-2 sm:mr-3">
                      <span className="text-purple-600 font-bold text-xs">2</span>
                    </div>
                    Channel & Platform Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-1.5">
                      <label className="block text-[11px] sm:text-sm font-bold text-gray-700 uppercase tracking-tight">Channel Name *</label>
                      <input
                        type="text"
                        name="channelName"
                        value={formData.channelName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200 text-sm sm:text-base"
                        placeholder="Enter channel name"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-[11px] sm:text-sm font-bold text-gray-700 uppercase tracking-tight">Platform *</label>
                      <select
                        name="platformName"
                        value={formData.platformName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200 text-sm sm:text-base"
                        required
                      >
                        <option value="">Select Platform</option>
                        {platforms.map((platform, index) => (
                          <option key={index} value={platform.name || platform}>
                            {platform.name || platform}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1.5">
                    <label className="block text-[11px] sm:text-sm font-bold text-gray-700 uppercase tracking-tight">Video Link *</label>
                    <input
                      type="url"
                      name="channelLink"
                      value={formData.channelLink}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200 text-sm sm:text-base"
                      placeholder="https://example.com/video-link"
                      required
                    />
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 sm:p-5">
                  <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-3 flex items-center">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-2 sm:mr-3">
                      <span className="text-yellow-600 font-bold text-xs">3</span>
                    </div>
                    Advertisement Timing
                  </h4>
                  <div className="space-y-1.5">
                    <label className="block text-[11px] sm:text-sm font-bold text-gray-700 uppercase tracking-tight">Ad Timing in Video *</label>
                    <input
                      type="text"
                      name="adTiming"
                      value={formData.adTiming}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 transition-all duration-200 text-sm sm:text-base"
                      placeholder="00:00 (MM:SS format)"
                      pattern="^([0-5]?[0-9]):([0-5]?[0-9])$"
                      title="Please enter time in MM:SS format (e.g., 01:30)"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Please specify the exact timing when the advertisement appears in the video (Format: MM:SS, e.g., 01:30 for 1 minute 30 seconds)
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 pt-4 shrink-0 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200 font-bold text-xs uppercase tracking-widest border border-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-[2] px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-black text-xs uppercase tracking-widest shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center space-x-2"
                  >
                    <FaUpload className="w-3 h-3" />
                    <span>Submit Proof</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Media Preview Modal */}
      {previewMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={closeMediaPreview}>
          <div className="max-w-4xl max-h-[90vh] relative" onClick={e => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 bg-white rounded-full p-2 hover:bg-gray-200 transition-colors"
              onClick={closeMediaPreview}
            >
              <FaTimes className="h-6 w-6" />
            </button>
            {previewMedia.type === 'video' ? (
              <video
                src={previewMedia.url}
                className="max-h-[80vh] max-w-full"
                controls
                autoPlay
              />
            ) : (
              <img
                src={previewMedia.url}
                alt="Preview"
                className="max-h-[80vh] max-w-full"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AcceptedFreeAds;
